// src/pages/DebtStudio/lib/buildPortfolioDetails.js
import dayjs from "lib/dayjs";
import {
  buildIssueCouponScheduleFromDeal,
  buildAnnualPeriods,
  couponToAnnualTotals,
} from "pages/DebtStudio/lib/buildPortfolioDebtService";

/* ------------------------------------------------------------------
 * Helpers
 * ------------------------------------------------------------------ */

function safeJson(value, fallback) {
  if (value == null) return fallback;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  }
  return value;
}

function num(x, dflt = 0) {
  const n = Number(x);
  return Number.isFinite(n) ? n : dflt;
}

function round2(x) {
  const n = num(x, 0);
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

function normPriority(v) {
  const s = String(v ?? "").trim().toLowerCase();
  if (s === "senior") return "senior";
  if (s === "subordinate" || s === "sub") return "subordinate";
  return "senior";
}

function getStructureRows(dealRow) {
  const raw = safeJson(dealRow?.structure, []);
  return Array.isArray(raw) ? raw : [];
}

function getRowPrincipal(r) {
  if (!r || typeof r !== "object") return 0;
  return (
    num(r.principal) ||
    num(r.par_amount) ||
    num(r.parAmount) ||
    num(r.amount) ||
    num(r.original_principal) ||
    0
  );
}

function getRowMaturityDate(r) {
  if (!r || typeof r !== "object") return null;

  const d =
    r.maturity_date ||
    r.maturityDate ||
    r.final_maturity_date ||
    r.finalMaturityDate ||
    // extra common variants
    r.maturity ||
    r.mat_date ||
    r.matDate ||
    r.maturity_dt ||
    r.maturityDt ||
    // IMPORTANT: structure rows frequently use "date" as the maturity
    r.date ||
    null;

  const dd = d ? dayjs(d) : null;
  return dd && dd.isValid() ? dd : null;
}

function getRowCallDate(r) {
  if (!r || typeof r !== "object") return null;

  const candidates = [
    r.call_date,
    r.callDate,
    r.first_call_date,
    r.firstCallDate,
    r.optional_call_date,
    r.optionalCallDate,
    r.first_optional_call_date,
    r.firstOptionalCallDate,
    r.callable_date,
    r.callableDate,

    // additional common variants
    r.call,
    r.first_call,
    r.optional_call,
    r.call_dt,
    r.callDt,
  ];

  for (const c of candidates) {
    const d = c ? dayjs(c) : null;
    if (d && d.isValid()) return d;
  }
  return null;
}

function sumOriginalPrincipal(dealRow) {
  const rows = getStructureRows(dealRow);
  let s = 0;
  for (const r of rows) s += getRowPrincipal(r);
  return round2(s);
}

function maxCouponDate(couponSchedule) {
  let m = null;
  for (const r of couponSchedule || []) {
    const d = dayjs(r.date);
    if (!m || d.isAfter(m)) m = d;
  }
  return m;
}

function maxStructureMaturityDate(dealRow) {
  const rows = getStructureRows(dealRow);
  let m = null;
  for (const r of rows) {
    const mat = getRowMaturityDate(r);
    if (!mat) continue;
    if (!m || mat.isAfter(m)) m = mat;
  }
  return m;
}

function labelForDeal(deal) {
  const issuer = deal?.issuers?.legal_name || deal?.issuer_name_raw || "";
  const issueName = deal?.name || deal?.deal_name || "(Untitled Issue)";
  return issuer ? `${issuer}, ${issueName}` : issueName;
}

/**
 * Find the index of the period that contains `asOf` using the same inclusion logic
 * as coupon assignment: (d > start) && (d <= end).
 *
 * Returns 0 if asOf is before the first period boundary.
 */
function findCurrentPeriodIndex(periods, asOf) {
  if (!periods?.length) return 0;
  const t = dayjs(asOf);

  if (t.isBefore(periods[0].start, "day")) return 0;

  const idx = periods.findIndex((p) => {
    const afterStart = t.isAfter(p.start, "day"); // strict
    const onOrBeforeEnd = t.isBefore(p.end, "day") || t.isSame(p.end, "day");
    return afterStart && onOrBeforeEnd;
  });

  return idx >= 0 ? idx : Math.max(0, periods.length - 1);
}

/**
 * Build annual periods WITHOUT requiring a deal-level dated date.
 * Uses a synthetic start date so the resulting periods include "as-of" and extend to horizonDate.
 */
function buildFiscalYearPeriodsFromAsOfToHorizon({
  fiscalYearEnd,
  asOf,
  horizonDate,
}) {
  const fy = dayjs(fiscalYearEnd);
  const hz = dayjs(horizonDate);
  const t = dayjs(asOf);

  if (!fy.isValid() || !hz.isValid() || !t.isValid()) return [];

  const fyMonth = fy.month();
  const fyDay = fy.date();

  const syntheticStart = t.subtract(2, "year").startOf("day");

  let firstEnd = syntheticStart.month(fyMonth).date(fyDay);
  if (!firstEnd.isAfter(syntheticStart, "day")) firstEnd = firstEnd.add(1, "year");

  const periods = [];
  let start = syntheticStart.clone();
  let end = firstEnd.clone();

  while (end.isBefore(hz, "day")) {
    periods.push({ start: start.clone(), end: end.clone() });
    start = end.clone();
    end = end.add(1, "year");
  }

  periods.push({ start: start.clone(), end: end.clone() });

  return periods;
}

/* ------------------------------------------------------------------
 * Series builders
 * ------------------------------------------------------------------ */

function buildOutstandingSeries({
  deal,
  couponSchedule,
  fiscalYearEnd,
  asOfDate = null,
}) {
  const datedDate = deal?.dated_date || deal?.datedDate || null;
  if (!datedDate || !fiscalYearEnd) return [];

  const last = maxCouponDate(couponSchedule);
  if (!last) return [];

  // NOTE: buildAnnualPeriods is generic anchored-annual logic (legacy callers used "fiscal")
  const periodsAll = buildAnnualPeriods(datedDate, fiscalYearEnd, last);
  if (!periodsAll.length) return [];

  const asOf = asOfDate ? dayjs(asOfDate) : dayjs();
  const startIdx = findCurrentPeriodIndex(periodsAll, asOf);
  const periods = periodsAll.slice(startIdx);

  const original = sumOriginalPrincipal(deal);

  const payments = (couponSchedule || [])
    .map((r) => ({ d: dayjs(r.date), principal: num(r.principal) }))
    .filter((x) => x.d.isValid() && x.principal !== 0)
    .sort((a, b) => a.d.valueOf() - b.d.valueOf());

  let paidToDate = 0;
  let payIdx = 0;

  return periods.map((p) => {
    const end = p.end;

    while (
      payIdx < payments.length &&
      (payments[payIdx].d.isBefore(end, "day") ||
        payments[payIdx].d.isSame(end, "day"))
    ) {
      paidToDate += payments[payIdx].principal;
      payIdx += 1;
    }

    const out = round2(Math.max(0, original - paidToDate));
    return { date: end.format("YYYY-MM-DD"), outstanding: out };
  });
}

function buildCallableByYearFromStructure(deal) {
  const rows = getStructureRows(deal);
  if (!rows.length) return new Map();

  const map = new Map();

  for (const r of rows) {
    const p = getRowPrincipal(r);
    if (p <= 0) continue;

    const call = getRowCallDate(r);
    if (!call) continue;

    const mat = getRowMaturityDate(r);
    if (mat && mat.isValid()) {
      if (mat.isBefore(call, "day")) continue;
    }

    const year = call.format("YYYY");
    const prev = num(map.get(year), 0);
    map.set(year, round2(prev + p));
  }

  return map;
}

/**
 * Callable vs Non-callable OUTSTANDING (stock) by annual period end buckets.
 *
 * Interpretation for charts:
 * - We include a maturity in a year bucket if it is outstanding at any point during the period
 *   i.e., maturity_date must be AFTER the period start (strict).
 *
 * This matches your expectation that a maturity in 2056 still appears in the 2056 bucket
 * even if it matures before the annual period end date.
 */
function buildCallableVsNoncallableOutstandingSeries({
  deal,
  fiscalYearEnd,
  asOfDate = null,
}) {
  if (!fiscalYearEnd) return [];

  const lastMat = maxStructureMaturityDate(deal);
  if (!lastMat) return [];

  const asOf = asOfDate ? dayjs(asOfDate) : dayjs();

  const periodsAll = buildFiscalYearPeriodsFromAsOfToHorizon({
    fiscalYearEnd,
    asOf,
    horizonDate: lastMat,
  });
  if (!periodsAll.length) return [];

  const startIdx = findCurrentPeriodIndex(periodsAll, asOf);
  const periods = periodsAll.slice(startIdx);

  const rows = getStructureRows(deal);

  return periods.map((p) => {
    const end = p.end;

    let callableRaw = 0;
    let noncallableRaw = 0;

    for (const r of rows) {
      const principal = getRowPrincipal(r);
      if (principal <= 0) continue;

      const mat = getRowMaturityDate(r);
      if (!mat) continue;

      // Key rule: include if outstanding at any point during the period
      // (maturity must be after the period start)
      if (!mat.isAfter(p.start, "day")) continue;

      const call = getRowCallDate(r);

      if (!call) {
        noncallableRaw += principal;
        continue;
      }

      if (call.isAfter(mat, "day")) {
        noncallableRaw += principal;
        continue;
      }

      // Treat callable for the period that contains the call date and all future periods
      if (call.isBefore(end, "day") || call.isSame(end, "day")) callableRaw += principal;
      else noncallableRaw += principal;
    }

    const callable = round2(callableRaw);
    const noncallable = round2(noncallableRaw);

    return {
      date: end.format("YYYY-MM-DD"),
      callable,
      noncallable,
      total: round2(callable + noncallable),
    };
  });
}

/* ------------------------------------------------------------------
 * Main builder
 * ------------------------------------------------------------------ */

export function buildPortfolioDetails({
  members = [],
  fiscalYearEndOverride = null,
  asOfDate = null,
} = {}) {
  const issues = [];

  for (const m of members || []) {
    const deal = m?.ds_deals || m?.ds_deals_row || m?.deal || null;
    if (!deal?.id) continue;
    if (m?.include === false) continue;

    const periodEnd =
      fiscalYearEndOverride ||
      deal.fiscal_year_end ||
      deal.fiscalYearEnd ||
      "";

    const couponSchedule = buildIssueCouponScheduleFromDeal(deal);
    const lienPriority = normPriority(deal?.lien_priority || deal?.lienPriority);
    const originalPrincipal = sumOriginalPrincipal(deal);

    const outstandingSeries = buildOutstandingSeries({
      deal,
      couponSchedule,
      fiscalYearEnd: periodEnd,
      asOfDate,
    });

    const datedDate = deal.dated_date || deal.datedDate || null;

    // NOTE: couponToAnnualTotals is the renamed, generic anchored-annual implementation
    const fiscalTotals = couponToAnnualTotals({
      couponSchedule,
      datedDate,
      periodEndAnchor: periodEnd,
      asOfDate,
    });

    const callableVsNoncallableOutstandingSeries =
      buildCallableVsNoncallableOutstandingSeries({
        deal,
        fiscalYearEnd: periodEnd,
        asOfDate,
      });

    issues.push({
      issueId: deal.id,
      label: labelForDeal(deal),
      deal,
      lienPriority,
      originalPrincipal,
      couponSchedule,
      outstandingSeries,
      fiscalTotals,
      callableVsNoncallableOutstandingSeries,
    });
  }

  /* -----------------------------
   * Outstanding by lien priority
   * ----------------------------- */

  const outstandingDates = new Set();
  for (const it of issues) {
    for (const r of it.outstandingSeries || []) outstandingDates.add(r.date);
  }
  const sortedOutstandingDates = Array.from(outstandingDates).sort(
    (a, b) => new Date(a) - new Date(b)
  );

  const outstandingByPriorityFY = sortedOutstandingDates.map((date) => {
    let senior = 0;
    let subordinate = 0;

    for (const it of issues) {
      const row = (it.outstandingSeries || []).find((x) => x.date === date);
      const v = row ? num(row.outstanding) : 0;
      if (it.lienPriority === "subordinate") subordinate += v;
      else senior += v;
    }

    senior = round2(senior);
    subordinate = round2(subordinate);

    return {
      date,
      senior,
      subordinate,
      total: round2(senior + subordinate),
    };
  });

  /* -----------------------------------------
   * Principal due by lien priority (scheduled)
   * ----------------------------------------- */

  const dueDates = new Set();
  for (const it of issues) {
    for (const r of it.fiscalTotals || []) dueDates.add(r.date);
  }
  const sortedDueDates = Array.from(dueDates).sort(
    (a, b) => new Date(a) - new Date(b)
  );

  const principalDueByPriorityFY = sortedDueDates.map((date) => {
    let seniorRaw = 0;
    let subRaw = 0;

    for (const it of issues) {
      const row = (it.fiscalTotals || []).find((x) => x.date === date);
      if (!row) continue;

      const p = num(row.principal, 0);
      if (it.lienPriority === "subordinate") subRaw += p;
      else seniorRaw += p;
    }

    const senior = round2(seniorRaw);
    const subordinate = round2(subRaw);

    return {
      date,
      senior,
      subordinate,
      total: round2(senior + subordinate),
    };
  });

  let compSenior = 0;
  let compSub = 0;

  for (const r of principalDueByPriorityFY) {
    compSenior += num(r.senior, 0);
    compSub += num(r.subordinate, 0);
  }

  const composition = {
    senior: round2(compSenior),
    subordinate: round2(compSub),
    total: round2(compSenior + compSub),
  };

  /* -----------------------------
   * Callable by year (flow)
   * ----------------------------- */

  const callYearMap = new Map();
  for (const it of issues) {
    const m = buildCallableByYearFromStructure(it.deal);
    for (const [year, amt] of m.entries()) {
      const prev = num(callYearMap.get(year), 0);
      callYearMap.set(year, round2(prev + amt));
    }
  }

  const callableByYear = Array.from(callYearMap.entries())
    .map(([year, callable_principal]) => ({ year, callable_principal }))
    .sort((a, b) => Number(a.year) - Number(b.year));

  /* -----------------------------------------
   * Callable vs non-callable outstanding (stock)
   * ----------------------------------------- */

  const callDates = new Set();
  for (const it of issues) {
    for (const r of it.callableVsNoncallableOutstandingSeries || [])
      callDates.add(r.date);
  }
  const sortedCallDates = Array.from(callDates).sort(
    (a, b) => new Date(a) - new Date(b)
  );

  const callableVsNoncallableOutstandingFY = sortedCallDates.map((date) => {
    let callableRaw = 0;
    let noncallableRaw = 0;

    for (const it of issues) {
      const row = (it.callableVsNoncallableOutstandingSeries || []).find(
        (x) => x.date === date
      );
      if (!row) continue;

      callableRaw += num(row.callable, 0);
      noncallableRaw += num(row.noncallable, 0);
    }

    const callable = round2(callableRaw);
    const noncallable = round2(noncallableRaw);

    return {
      date,
      callable,
      noncallable,
      total: round2(callable + noncallable),
    };
  });

  return {
    issues,
    outstandingByPriorityFY,
    principalDueByPriorityFY,
    composition,
    callableByYear,
    callableVsNoncallableOutstandingFY,
  };
}
